// utils/memberProfileThemeDisplay.js
// Maps a profileTheme payload (legacy effectKey + overlayEffectKeys) to UI flags.

const OVERLAY_KEYS = new Set([
  'rain',
  'snow',
  'lightning',
  'drip',
  'radioactive-drip',
  'shooting-stars',
  'nebula',
  'starfield',
  'heat',
]);

export function normalizeOverlayEffectKeys(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const item of raw) {
    const key = String(item ?? '').trim().toLowerCase();
    if (!OVERLAY_KEYS.has(key) || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  if (out.includes('drip') && out.includes('radioactive-drip')) {
    return out.filter((k) => k !== 'drip');
  }
  if (out.includes('rain') && out.includes('snow')) {
    return out.filter((k) => k !== 'rain');
  }
  return out;
}

function clampInt(value, min, max, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, Math.round(n)));
}

function normalizeHaloColor(raw) {
  if (raw == null || raw === '') return null;
  const hex = String(raw).trim();
  if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) return null;
  return hex.toLowerCase();
}

export function normalizeEffectConfig(raw) {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return {
      rainDensity: 100,
      snowDensity: 55,
      snowWind: 35,
      dripDensity: 100,
      lightningQuantity: 50,
      lightningHeight: 45,
      shootingStarsQuantity: 50,
      nebulaQuantity: 55,
      nebulaInclination: 50,
      nebulaMotion: 40,
      starfieldDensity: 50,
      starfieldDistribution: 45,
      haloColor: null,
      haloSpiral: 0,
      haloFrozen: 0,
      heatIntensity: 0,
      bannerVideoDarken: 0,
      cardVideoDarken: 0,
    };
  }
  const cfg = {
    rainDensity: clampInt(raw.rainDensity, 0, 100, 100),
    snowDensity: clampInt(raw.snowDensity, 0, 100, 55),
    snowWind: clampInt(raw.snowWind, 0, 100, 35),
    dripDensity: clampInt(raw.dripDensity, 0, 100, 100),
    lightningQuantity: clampInt(raw.lightningQuantity, 0, 100, 50),
    lightningHeight: clampInt(raw.lightningHeight, 0, 100, 45),
    shootingStarsQuantity: clampInt(raw.shootingStarsQuantity, 0, 100, 50),
    nebulaQuantity: clampInt(raw.nebulaQuantity, 0, 100, 55),
    nebulaInclination: clampInt(raw.nebulaInclination, 0, 100, 50),
    nebulaMotion: clampInt(raw.nebulaMotion, 0, 100, 40),
    starfieldDensity: clampInt(raw.starfieldDensity, 0, 100, 50),
    starfieldDistribution: clampInt(raw.starfieldDistribution, 0, 100, 45),
    haloColor: normalizeHaloColor(raw.haloColor),
    haloSpiral: clampInt(raw.haloSpiral, 0, 100, 0),
    haloFrozen: clampInt(raw.haloFrozen, 0, 100, 0),
    heatIntensity: clampInt(raw.heatIntensity, 0, 100, 0),
    bannerVideoDarken: clampInt(raw.bannerVideoDarken, 0, 100, 0),
    cardVideoDarken: clampInt(raw.cardVideoDarken, 0, 100, 0),
  };
  if (cfg.haloSpiral > 0 && cfg.haloFrozen > 0) {
    cfg.haloSpiral = 0;
  }
  return cfg;
}

/** Map 0–100 height slider → layer height % (25–100). Higher = bolts reach lower. */
export function lightningHeightToPercent(heightSlider) {
  const h = clampInt(heightSlider, 0, 100, 45);
  return 25 + Math.round(h * 0.75);
}

/** Discrete class for CSP-safe lightning layer height (steps of 5%). */
export function lightningHeightClass(heightSlider) {
  const pct = lightningHeightToPercent(heightSlider);
  const step = Math.round(pct / 5) * 5;
  const clamped = Math.max(25, Math.min(100, step));
  return `perk-theme-overlay__layer--lightning-h${clamped}`;
}

function themeHasMedia(profileTheme) {
  return Boolean(
    profileTheme?.bannerMediaUrl
    || profileTheme?.cardMediaUrl
    || profileTheme?.backgroundMediaUrl,
  );
}

export function resolveProfileThemeEffects(profileTheme) {
  const effectKey = profileTheme?.effectKey || null;
  const hasMedia = themeHasMedia(profileTheme);
  const overlayEffectKeys = !effectKey
    ? normalizeOverlayEffectKeys(profileTheme?.overlayEffectKeys)
    : [];
  const effectConfig = normalizeEffectConfig(profileTheme?.effectConfig);
  const hasOverlays = overlayEffectKeys.length > 0;

  if (!effectKey) {
    return {
      hasTheme: hasMedia || hasOverlays,
      // Legacy CSS space pack (blue nebula + starfield) — never for overlays-only.
      showDonorSpace: false,
      isPremiumEffect: false,
      showNebulaMorph: false,
      showShootingStars: false,
      effectKey: null,
      overlayEffectKeys,
      effectConfig,
      bannerMediaUrl: profileTheme?.bannerMediaUrl || null,
      bannerMediaType: profileTheme?.bannerMediaType || null,
      cardMediaUrl: profileTheme?.cardMediaUrl || null,
      cardMediaType: profileTheme?.cardMediaType || null,
    };
  }

  const showDonorSpace =
    effectKey === 'nebula'
    || effectKey === 'shooting-stars'
    || effectKey === 'shooting-star-premium'
    || hasMedia;
  const isPremiumEffect = effectKey === 'shooting-star-premium';
  const showShootingStars =
    effectKey === 'shooting-stars' || effectKey === 'shooting-star-premium';

  return {
    hasTheme: true,
    showDonorSpace,
    isPremiumEffect,
    showNebulaMorph: isPremiumEffect,
    showShootingStars,
    effectKey,
    overlayEffectKeys: [],
    effectConfig,
    bannerMediaUrl: profileTheme?.bannerMediaUrl || null,
    bannerMediaType: profileTheme?.bannerMediaType || null,
    cardMediaUrl: profileTheme?.cardMediaUrl || null,
    cardMediaType: profileTheme?.cardMediaType || null,
  };
}

export function isHlsMedia(url, mediaType) {
  const type = String(mediaType || '').toLowerCase();
  if (type === 'hls' || type === 'video/hls' || type === 'video') return true;
  return String(url || '').toLowerCase().split('?')[0].endsWith('.m3u8');
}

/** Video darken overlay amount (0–100). Images bake darken at upload — ignore here. */
export function resolveVideoDarken(url, mediaType, amount) {
  if (!isHlsMedia(url, mediaType)) return 0;
  const n = Number(amount);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}
